import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { BUILDINGS } from "@/pages/Rooms";
import api from "@/lib/api";
import { loadError } from "@/lib/api";
import { euro, STATUS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { TrendingUp, Percent, Wallet, Gauge, Download, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { downloadBlob } from "@/lib/download";

const COLORS = ["#C86D51", "#4A6B5D", "#1F8A5B", "#D4AF37", "#78909C"];

const Kpi = ({ icon: Icon, label, value, sub, tint }) => (
  <Card className="p-5 border-border shadow-sm animate-fade-up">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
        <p className="font-heading text-2xl sm:text-3xl font-semibold mt-2">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </div>
      <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: tint }}><Icon className="w-5 h-5 text-white" /></div>
    </div>
  </Card>
);

export default function Reports() {
  const { building } = useParams();
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  useEffect(() => { setData(null); api.get("/reports/summary", { params: { building } }).then((r) => setData(r.data)).catch(loadError); }, [building]);
  if (!BUILDINGS[building]) return <Navigate to="/report/nord" replace />;
  if (!data) return null;

  const pie = Object.entries(data.status_counts || {}).map(([k, v]) => ({ name: STATUS[k]?.label || k, value: v }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold inline-flex items-center gap-2"><Building2 className="w-3.5 h-3.5" />Analisi · {BUILDINGS[building]}</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-1" data-testid="reports-title">Report Incassi {building === "nord" ? "Nord" : "Sud"}</h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="reports-scope">{data.total_rooms} camere · incassi, occupazione e prenotazioni delle sole camere dell'{BUILDINGS[building]}</p>
        </div>
        <div className="flex items-end gap-2" data-testid="monthly-export">
          <div>
            <Label className="text-xs uppercase tracking-wider">Mese</Label>
            <Input type="month" data-testid="export-month-input" className="mt-1.5 w-[170px]" value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>
          <Button data-testid="export-invoices-csv" onClick={() => downloadBlob(`/invoices/export.csv?month=${month}`, `documenti_${month}.csv`, "CSV scaricato")} className="bg-accent hover:bg-accent/90 text-accent-foreground">
            <Download className="w-4 h-4 mr-2" />Scarica CSV documenti
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={TrendingUp} label="Incasso totale" value={euro(data.total_income)} sub={`Questo mese: ${euro(data.month_income)}`} tint="#4A6B5D" />
        <Kpi icon={Percent} label="Occupazione" value={`${data.occupancy}%`} sub="mese corrente" tint="#C86D51" />
        <Kpi icon={Wallet} label="ADR" value={euro(data.adr)} sub="prezzo medio/notte" tint="#D4AF37" />
        <Kpi icon={Gauge} label="RevPAR" value={euro(data.revpar)} sub="ricavo per camera" tint="#1F8A5B" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 p-6 border-border shadow-sm">
          <h2 className="font-heading text-lg font-semibold mb-4">Incassi ultimi 6 mesi</h2>
          <ResponsiveContainer width="100%" height={300} data-testid="revenue-chart">
            <BarChart data={data.monthly_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E2D9" vertical={false} />
              <XAxis dataKey="month" stroke="#8b7d72" fontSize={12} />
              <YAxis stroke="#8b7d72" fontSize={12} />
              <Tooltip formatter={(v) => euro(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E8E2D9" }} />
              <Bar dataKey="incasso" fill="#C86D51" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 border-border shadow-sm">
          <h2 className="font-heading text-lg font-semibold mb-4">Prenotazioni per stato</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={85} label>
                {pie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
